import isBoolean from 'lodash/isBoolean';
import i18next from 'i18next';

import { notificationTypes, paneContentModes, slateTypes } from 'config/constants';
import { showNotification } from './notification';
import * as type from './types';

function shouldDockByDefault(state) {
  return (
    window.matchMedia('(min-width: 991px)').matches &&
    !state.paneUnpinnedByUser
  );
}

function persistSlatePaneAttributes(stateAttributes) {
  try {
    if (isBoolean(stateAttributes.isPaneOpen)) {
      sessionStorage.setItem('slatePaneOpen', String(stateAttributes.isPaneOpen));
    }
    if (isBoolean(stateAttributes.isPaneHidden)) {
      sessionStorage.setItem('slatePaneHidden', String(stateAttributes.isPaneHidden));
    }
    if (isBoolean(stateAttributes.isPaneDocked)) {
      sessionStorage.setItem('slatePaneDocked', String(stateAttributes.isPaneDocked));
    }
    if (isBoolean(stateAttributes.paneUnpinnedByUser)) {
      sessionStorage.setItem('slatePaneUnpinned', String(stateAttributes.paneUnpinnedByUser));
    }
    if (stateAttributes.contentJson !== undefined) {
      if (stateAttributes.contentJson === null) {
        sessionStorage.removeItem('slatePaneContent');
      } else {
        sessionStorage.setItem('slatePaneContent', JSON.stringify(stateAttributes.contentJson));
        sessionStorage.removeItem('slatePaneSlateUUID');
      }
    }
    if (stateAttributes.contentMode !== undefined) {
      if (stateAttributes.contentMode === null) {
        sessionStorage.removeItem('slatePaneContentMode');
      } else {
        sessionStorage.setItem('slatePaneContentMode', stateAttributes.contentMode);
      }
    }
  } catch (e) { /* ignore */ }
}

export function updateSlatePane(stateAttributes) {
  return function (dispatch) {
    persistSlatePaneAttributes(stateAttributes);
    dispatch({
      type: type.UPDATE_SLATE_PANE_STATE,
      payload: stateAttributes,
    });
  };
}

export function openSlateInPane(slateUUID, options) {
  return function (dispatch, getState) {
    if (!slateUUID) return;

    var opts = options || {};
    var state = getState().slatePane;

    var payload = {
      isPaneHidden: false,
      slateInSlatePaneUUID: slateUUID,
      contentJson: null,
      contentMode: null,
      contentModified: false,
      liveContentData: null,
      slatePaneNavList: opts.navList || [],
    };
    // The type travels with the click: a nav list is always one type, so
    // prev/next pass it back unchanged (SlatePaneManager).
    var slateType = opts.slateType || slateTypes.slate;
    payload.slatePaneSlateType = slateType;
    // Same UUID reopened: force the iframe to re-navigate. Must ride in the
    // same dispatch as the type so the navigate sends the new hint.
    if (slateUUID === state.slateInSlatePaneUUID) {
      payload.slatePaneForceRefresh = Date.now();
    }
    if (!state.isPaneDocked) {
      payload.isPaneOpen = true;
    }

    var dock = opts.dock;
    if (dock === undefined) {
      dock = shouldDockByDefault(state);
    }
    if (dock) {
      payload.isPaneOpen = true;
      payload.isPaneDocked = true;
    }

    // Clear pane-owned sessionStorage (replacing any previous content)
    try {
      sessionStorage.removeItem('slatePaneContent');
      sessionStorage.setItem('slatePaneContentMode', paneContentModes.SLATE);
      sessionStorage.setItem('slatePaneSlateUUID', slateUUID);
      sessionStorage.setItem('slatePaneSlateType', slateType);
      sessionStorage.setItem('slatePaneOpen', 'true');
      sessionStorage.setItem('slatePaneHidden', 'false');
      if (payload.isPaneDocked !== undefined) {
        sessionStorage.setItem('slatePaneDocked', String(payload.isPaneDocked));
      }
      if (opts.navList) {
        sessionStorage.setItem('slatePaneNavList', JSON.stringify(opts.navList));
      }
    } catch (e) { /* ignore */ }

    dispatch({
      type: type.UPDATE_SLATE_PANE_STATE,
      payload: payload,
    });
  };
}

export function openContentInPane(json, mode, options) {
  return function (dispatch, getState) {
    var opts = options || {};
    var state = getState().slatePane;

    var payload = {
      isPaneOpen: true,
      isPaneHidden: false,
      contentJson: json,
      contentMode: mode,
      slateInSlatePaneUUID: null,
      slatePaneNavList: [],
      slatePaneSlateType: slateTypes.slate,
      contentModified: false,
      liveContentData: null,
    };

    var dock = opts.dock;
    if (dock === undefined) {
      dock = shouldDockByDefault(state);
    }
    if (dock) {
      payload.isPaneDocked = true;
    }

    // Write pane-owned sessionStorage for refresh recovery
    try {
      sessionStorage.setItem('slatePaneContent', JSON.stringify(json));
      sessionStorage.setItem('slatePaneContentMode', mode);
      sessionStorage.removeItem('slatePaneSlateUUID');
      sessionStorage.removeItem('slatePaneSlateType');
      sessionStorage.setItem('slatePaneOpen', 'true');
      sessionStorage.setItem('slatePaneHidden', 'false');
      if (payload.isPaneDocked !== undefined) {
        sessionStorage.setItem('slatePaneDocked', String(payload.isPaneDocked));
      }
    } catch (e) { /* ignore */ }

    dispatch({
      type: type.UPDATE_SLATE_PANE_STATE,
      payload: payload,
    });
  };
}

export function openTemplateInPane(json, options) {
  return function (dispatch) {
    dispatch(openContentInPane(json, paneContentModes.TEMPLATE, options));
  };
}

export function openDemoInPane(json, options) {
  return function (dispatch) {
    dispatch(openContentInPane(json, paneContentModes.DEMO, options));
  };
}

export function closeSlatePane() {
  return function (dispatch) {
    // Clear pane-owned sessionStorage
    clearPaneSessionStorage();

    dispatch({
      type: type.UPDATE_SLATE_PANE_STATE,
      payload: {
        isPaneOpen: false,
        isPaneHidden: false,
        isPaneDocked: false,
        paneUnpinnedByUser: false,
        slateInSlatePaneUUID: null,
        contentJson: null,
        contentMode: null,
        contentModified: false,
        liveContentData: null,
        slatePaneNavList: [],
        slatePaneSlateType: slateTypes.slate,
        slatePaneDockedTabs: [],
      },
    });
  };
}

export function clearPaneSessionStorage() {
  try {
    sessionStorage.removeItem('slatePaneContent');
    sessionStorage.removeItem('slatePaneContentMode');
    sessionStorage.removeItem('slatePaneSlateUUID');
    sessionStorage.removeItem('slatePaneSlateType');
    sessionStorage.removeItem('slatePaneOpen');
    sessionStorage.removeItem('slatePaneHidden');
    sessionStorage.removeItem('slatePaneNavList');
    sessionStorage.removeItem('slatePaneDocked');
    sessionStorage.removeItem('slatePaneUnpinned');
    sessionStorage.removeItem('slateMateChatSessionUuid');
    sessionStorage.removeItem('slateMateChatMessages');
    sessionStorage.removeItem('slateMateChatAttachments');
  } catch (e) { /* ignore */ }
}

// Content lifecycle actions

export function slatePaneContentLoaded(uuid, mode) {
  return function (dispatch) {
    try {
      sessionStorage.setItem('slatePaneContentMode', mode);
    } catch (e) { /* ignore */ }

    dispatch({
      type: type.SLATE_PANE_CONTENT_LOADED,
      payload: { uuid: uuid, mode: mode },
    });
  };
}

export function slatePaneContentChanged(slateData) {
  return function (dispatch) {
    if (slateData != null) {
      try {
        sessionStorage.setItem('slatePaneContent', JSON.stringify(slateData));
      } catch (e) { /* ignore */ }
    }

    dispatch({
      type: type.SLATE_PANE_CONTENT_CHANGED,
      payload: { slateData: slateData },
    });
  };
}

export function slatePaneContentSaved() {
  return function (dispatch, getState) {
    try {
      sessionStorage.removeItem('slatePaneContent');
    } catch (e) { /* ignore */ }

    var contentMode = getState().slatePane.contentMode;
    var notificationKey = 'notifications:slatePaneSlateSaved';
    if (contentMode === paneContentModes.TEMPLATE) {
      notificationKey = 'notifications:slatePaneTemplateSaved';
    } else if (contentMode === paneContentModes.DEMO) {
      notificationKey = 'notifications:slatePaneDemoSaved';
    }

    dispatch(showNotification(
      notificationTypes.SUCCESS,
      i18next.t(notificationKey),
    ));

    dispatch({
      type: type.SLATE_PANE_CONTENT_SAVED,
    });
  };
}
